import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  limit,
} from "firebase/firestore";
import { toast } from "react-toastify";

// Routes a customer (anyone with a public share token) can land on. Even if
// a business admin happens to also be authenticated in the same browser, we
// don't want admin-only toast notifications popping over the public page —
// that leaks "Invoice Viewed" / "New Booking" etc. to whoever is looking.
const PUBLIC_ROUTE_PREFIXES = [
  "/invoice/",
  "/estimate/",
  "/booking/",
  "/contract/",
  "/quote/",
  "/proposal/",
  "/bill-of-sale/",
  "/share/",
  "/accept-invoice",
  "/accept-estimate",
  "/accept-booking",
  "/pay",
  "/pay-deposit",
];

function isPublicDocumentPath(pathname) {
  return PUBLIC_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

// Map Firestore notification type → preferences key
const TYPE_TO_PREF_KEY = {
  booking_created: "booking_created",
  booking_confirmed: "booking_confirmed",
  booking_cancelled: "booking_cancelled",
  booking_reminder: "booking_reminder",
  estimate_accepted: "estimate_accepted",
  estimate_rejected: "estimate_rejected",
  estimate_expiring: "estimate_expiring",
  deposit_paid: "deposit_paid",
  invoice_paid: "invoice_paid",
  invoice_overdue: "invoice_overdue",
  payment_received: "payment_received",
  payment_failed: "payment_failed",
  task_completed: "task_completed",
  employee_clock_in: "employee_clock_in",
  employee_message: "employee_message",
  new_request: "new_request",
  recurring_job_due: "recurring_job_due",
  low_inventory: "low_inventory",
  customer_birthday: "customer_birthday",
  customer_anniversary: "customer_anniversary",
  review_request_sent: "review_request_sent",
};

function isInQuietHours(quietHours) {
  if (!quietHours?.enabled) return false;
  try {
    const now = new Date();
    const [startH, startM] = (quietHours.start || "22:00").split(":").map(Number);
    const [endH, endM] = (quietHours.end || "07:00").split(":").map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const startMins = startH * 60 + startM;
    const endMins = endH * 60 + endM;
    // Overnight range (e.g. 22:00 → 07:00)
    if (startMins > endMins) return nowMins >= startMins || nowMins < endMins;
    return nowMins >= startMins && nowMins < endMins;
  } catch {
    return false;
  }
}

function isNotifAllowed(notif, prefs) {
  if (!prefs) return true; // no prefs yet → allow everything
  const channels = prefs.channels || {};
  if (channels.inApp === false) return false; // global inApp off
  if (isInQuietHours(prefs.quietHours)) return false;
  const prefKey = TYPE_TO_PREF_KEY[notif.type];
  if (prefKey) {
    const perType = (prefs.notifications || {})[prefKey];
    if (perType && perType.inApp === false) return false;
  }
  return true;
}

export default function NotificationListener() {
  const seenIds = useRef(new Set());
  const initializedRef = useRef(false);
  const prefsRef = useRef(null);
  const { pathname } = useLocation();
  const onPublicPage = isPublicDocumentPath(pathname);

  useEffect(() => {
    // Don't surface admin notifications while the browser is on a customer-
    // facing share / payment route, even if the user is authenticated.
    if (onPublicPage) return;

    let unsubNotifs = null;
    let unsubPrefs = null;

    const unsubAuth = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) return;

      try {
        const userSnap = await getDoc(doc(db, "users", user.uid));
        const bId = userSnap.data()?.businessId;
        if (!bId) return;

        // Load preferences once and keep a ref (re-fetch on change would require another listener)
        const prefsSnap = await getDoc(
          doc(db, `businesses/${bId}/settings/notificationPreferences`)
        );
        prefsRef.current = prefsSnap.exists() ? prefsSnap.data() : null;

        // Real-time listener — only the 50 most recent to keep it lightweight
        const q = query(
          collection(db, `businesses/${bId}/notifications`),
          orderBy("timestamp", "desc"),
          limit(50)
        );

        unsubNotifs = onSnapshot(q, (snap) => {
          if (!initializedRef.current) {
            // Seed seen IDs on first load so we don't toast for old notifications
            snap.docs.forEach((d) => seenIds.current.add(d.id));
            initializedRef.current = true;
            return;
          }

          snap.docChanges().forEach((change) => {
            if (change.type !== "added") return;
            const notif = { id: change.doc.id, ...change.doc.data() };
            if (notif.forEmployee) return;
            if (seenIds.current.has(notif.id)) return;
            seenIds.current.add(notif.id);

            if (!isNotifAllowed(notif, prefsRef.current)) return;

            const title = notif.title || "New Notification";
            const message = notif.message || "";

            toast.info(message ? `${title}: ${message}` : title, {
              toastId: notif.id,
              autoClose: 5000,
              position: "top-right",
            });

            // Browser notification (if permission granted and not in focus)
            if (
              "Notification" in window &&
              Notification.permission === "granted" &&
              document.hidden
            ) {
              new Notification(title, {
                body: message,
                icon: "/logo192.png",
                tag: notif.id,
              });
            }
          });
        });
      } catch (e) {
        console.error("NotificationListener error:", e);
      }
    });

    return () => {
      unsubAuth();
      if (unsubNotifs) unsubNotifs();
      if (unsubPrefs) unsubPrefs();
    };
  }, [onPublicPage]);

  return null;
}
